import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Lock, Scissors, Search, Shirt } from 'lucide-react';
import { listInvoices, fetchInvoice, assignWork } from '../../api/resources';
import { useToast } from '../../context/ToastContext';
import Modal from '../ui/Modal';
import Spinner from '../ui/Spinner';

/**
 * Hand work to one worker, starting from the worker rather than the order.
 *
 * The floor often works the other way round from the office: somebody is free
 * now, so what can they be given? This finds the order, shows its garments,
 * and hands a job over in one tap.
 *
 * Two steps on purpose — pick the order, then the garment — because an order
 * number is what the shop has in its head, and a garment only means something
 * once the order is known.
 */

const WORK = [
  { value: 'CUTTING', label: 'Cutting', Icon: Scissors, roles: ['CUTTING_MASTER', 'ALL_ROUNDER'] },
  { value: 'STITCHING', label: 'Stitching', Icon: Shirt, roles: ['SEWING_TAILOR', 'ALL_ROUNDER'] },
];

export default function AssignWorkModal({ worker, open, onClose }) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [query, setQuery] = useState('');
  const [orderId, setOrderId] = useState(null);

  const skillIds = new Set((worker?.skills || []).map((s) => s.id));
  // A skill is given on the set, because that is what the catalogue lists —
  // nobody is asked whether a tailor can make the Pant of a Coat Pant. So
  // somebody skilled in the set is skilled in every part of it.
  const canMake = (it) => skillIds.has(it.tailoringItemId) || skillIds.has(it.setItemId);

  const typed = query.trim();

  // With nothing typed, the latest orders — most work handed out is for
  // something taken in today, so the order wanted is usually already here and
  // needs no searching at all.
  const { data: found, isFetching } = useQuery({
    queryKey: ['assign-orders', typed],
    queryFn: () => listInvoices(typed
      ? { search: typed, page: 1, perPage: 25 }
      : { page: 1, perPage: 8 }),
    enabled: open,
  });

  const { data: full, isLoading: loadingOrder } = useQuery({
    queryKey: ['invoice', String(orderId)],
    queryFn: () => fetchInvoice(orderId),
    enabled: !!orderId,
  });

  const mut = useMutation({
    mutationFn: (body) => assignWork(body),
    onSuccess: (d) => {
      success(d.message || 'Work handed out.');
      qc.invalidateQueries({ queryKey: ['invoice', String(orderId)] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['worker-workload'] });
    },
    onError: (e) => error(e.response?.data?.message || 'Could not hand out the work.'),
  });

  const close = () => { setQuery(''); setOrderId(null); onClose(); };
  const order = full?.invoice;

  return (
    <Modal
      open={open}
      onClose={close}
      title={worker ? `Give work to ${worker.ledgerName || worker.name}` : 'Give work'}
      subtitle={worker?.role ? worker.role.replace(/_/g, ' ').toLowerCase() : undefined}
      size="lg"
    >
      {!orderId ? (
        <>
          <div className="relative">
            <Search size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-content-subtle" />
            <input
              className="input pl-8"
              autoFocus
              placeholder="Invoice number, customer name or mobile…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <p className="mt-3 text-2xs font-bold uppercase tracking-wider text-content-subtle">
            {typed ? 'Matching orders' : 'Recent orders'}
          </p>

          <div className="mt-1 max-h-80 space-y-1 overflow-y-auto">
            {isFetching && <Spinner label={typed ? 'Searching…' : 'Loading…'} />}
            {!isFetching && (found?.invoices || []).length === 0 && (
              <p className="py-8 text-center text-sm italic text-content-subtle">
                {typed ? `Nothing matches “${typed}”.` : 'No orders yet.'}
              </p>
            )}
            {(found?.invoices || []).map((inv) => (
              <button
                key={inv.id}
                type="button"
                className="flex w-full items-center gap-3 rounded-lg border border-line px-3 py-2 text-left hover:border-brand-500 hover:bg-brand-50/40"
                onClick={() => setOrderId(inv.id)}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-brand-700">{inv.orderNo}</span>
                  <span className="block truncate text-xs text-content-muted">
                    {inv.customer?.name || '—'}
                    {inv.itemsText ? ` · ${inv.itemsText}` : ''}
                  </span>
                </span>
                <span className="shrink-0 text-2xs text-content-subtle">
                  {inv.deliveryDate ? `due ${inv.deliveryDate}` : 'no date'}
                </span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <button
            type="button"
            className="btn-ghost btn-sm -ml-2 mb-2"
            onClick={() => setOrderId(null)}
          >
            <ChevronLeft size={14} /> Another order
          </button>

          {loadingOrder && <Spinner label="Loading the order…" />}

          {order && (
            <>
              <p className="mb-2 text-sm">
                <b className="text-content">{order.orderNo}</b>
                <span className="text-content-muted"> · {order.customer?.name || '—'}</span>
              </p>

              <div className="overflow-x-auto rounded-lg border border-line">
                <table className="w-full min-w-[32rem] text-sm">
                  <thead className="bg-surface-sunken text-left text-2xs font-bold uppercase tracking-wide text-content-muted">
                    <tr>
                      <th className="px-3 py-1.5">Garment</th>
                      <th className="px-3 py-1.5">Cutting</th>
                      <th className="px-3 py-1.5">Stitching</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(order.items || []).map((it) => {
                      const pieces = Math.max(1, Number(it.qty) || 1);
                      // A line of more than one garment is handed out piece by
                      // piece, the same as on the order screen.
                      return Array.from({ length: pieces }, (_, p) => p + 1).map((pieceNo) => (
                        <tr key={`${it.id}-${pieceNo}`} className="border-t border-line">
                          <td className="px-3 py-1.5">
                            <span className="font-medium text-content">{it.itemName}</span>
                            {pieces > 1 && (
                              <span className="ml-1 text-2xs text-content-subtle">#{pieceNo} of {pieces}</span>
                            )}
                            {/* The worker's skills are a guide, not a gate —
                                somebody has to sew it tonight either way. */}
                            {it.tailoringItemId && !canMake(it) && (
                              <span className="ml-1.5 text-2xs text-warning-700" title="Not one of their skills">
                                not their skill
                              </span>
                            )}
                          </td>
                          {WORK.map((w) => {
                            const held = (it.assignments || []).find(
                              (a) => a.workType === w.value && (a.pieceNo || 1) === pieceNo,
                            );
                            const mine = held && Number(held.workerId) === worker?.id;
                            const fits = !worker?.role || w.roles.includes(worker.role);
                            return (
                              <td key={w.value} className="px-3 py-1.5">
                                {held?.settled ? (
                                  <span className="inline-flex items-center gap-1 text-2xs font-semibold text-brand-700">
                                    <Lock size={11} /> settled
                                  </span>
                                ) : mine ? (
                                  <span className="badge-success">theirs</span>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn-outline btn-sm"
                                    disabled={mut.isPending}
                                    title={held
                                      ? `Take it from ${held.workerName}`
                                      : fits ? 'Give this job to them' : `${w.label} is not their kind of work`}
                                    onClick={() => mut.mutate({
                                      workerId: worker.id,
                                      invoiceItemId: it.id,
                                      workType: w.value,
                                      pieceNo,
                                    })}
                                  >
                                    <w.Icon size={12} />
                                    {held ? `Take from ${held.workerName?.split(' ')[0]}` : 'Give'}
                                  </button>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ));
                    })}
                  </tbody>
                </table>
              </div>

              <p className="mt-2 text-2xs text-content-subtle">
                The cost comes from the rate card when the job is handed out. Settled work cannot be moved.
              </p>
            </>
          )}
        </>
      )}
    </Modal>
  );
}
